// domain/model/TargetLifecycle.js
import { TARGET_STATUS, TARGET_STATUS_META } from '../constants/targetStatus';

/**
 * TargetLifecycle — where a target has got to, and the dates that record how it got there.
 *
 * A VALUE OBJECT: no identity, frozen, equal to another lifecycle when every field matches. Two
 * targets both waiting with no dates are not the same target, but they do hold the same lifecycle,
 * and nothing needs to tell one instance from another.
 *
 * WHY THIS EXISTS AS A TYPE. The status and its four dates were five loose fields, and four places
 * had to know they were one thing: `applyStatusChange` reached into the target for the date it might
 * stamp, `findDateConflicts` re-derived the same rules to judge one, the importer filtered that
 * function's output to get at half of it, and `TargetCard.setTargetStatus` inlined the whole table a
 * fifth time. Date rules spread across an entity and three services is the classic shape of a
 * missing value object — the same reasoning that produced `EffectivePeriod` in revenueCycle.
 *
 * THE TWO HALVES ARE TWO OPERATIONS, and separating them is most of the point. Moving a target to a
 * new status CLEARS the dates that status contradicts, and STAMPS the one it requires. An importer
 * wants the first and must not do the second: stamping "now" for a mastery date the file never gave
 * would write clinical data nobody supplied. As one function with a timestamp argument, the caller
 * had to pass a sentinel and filter the result to separate them. As two, it just calls one.
 *
 * @domainObject value-object
 * @invariant Frozen. A lifecycle is replaced, never edited — which is what makes it impossible to
 *   move the status and leave a date behind.
 * @invariant A status outside the built-in seven is legitimate (a workflow may define its own) and
 *   carries no date rules, so it clears nothing and requires nothing.
 * @typedef {Object} TargetLifecycle
 * @property {string} status - where the target has got to
 * @property {*} introductionDate - when teaching began
 * @property {*} masteryDate - when the target was mastered
 * @property {*} holdDate - when it was put on hold
 * @property {*} abortedDate - when it was discontinued
 */

/** The dates a lifecycle holds, in the order they are reported. */
export const LIFECYCLE_DATES = ['introductionDate', 'masteryDate', 'holdDate', 'abortedDate'];

/**
 * @param {Object} attrs
 * @returns {TargetLifecycle} frozen — a value object has no mutators
 */
export function createTargetLifecycle(attrs = {}) {
  return Object.freeze({
    status: attrs.status ?? null,
    introductionDate: attrs.introductionDate ?? null,
    masteryDate: attrs.masteryDate ?? null,
    holdDate: attrs.holdDate ?? null,
    abortedDate: attrs.abortedDate ?? null,
  });
} // createTargetLifecycle()

/**
 * Move to a status, clearing the dates it contradicts.
 *
 * Stamps nothing. A caller that has a real timestamp to record follows this with
 * `stampRequiredDate`; one reading a file does not, and calling only this is how it says so.
 *
 * @param {TargetLifecycle} lifecycle
 * @param {string} nextStatus
 * @returns {TargetLifecycle}
 */
export function withStatus(lifecycle, nextStatus) {
  const meta = TARGET_STATUS_META[nextStatus];
  const cleared = (meta?.resetsDates ?? [])
    .reduce((acc, dateField) => ({ ...acc, [dateField]: null }), {});

  return createTargetLifecycle({ ...lifecycle, ...cleared, status: nextStatus });
} // withStatus()

/**
 * Record the date the current status requires, if it is not already recorded.
 *
 * NEVER OVERWRITES. Re-entering `mastered` must not move the day the target was actually mastered.
 *
 * @param {TargetLifecycle} lifecycle
 * @param {*} now - the moment to record; the domain does not read a clock
 * @returns {TargetLifecycle}
 */
export function stampRequiredDate(lifecycle, now) {
  const required = TARGET_STATUS_META[lifecycle.status]?.stampsDate;

  if (!required || lifecycle[required]) {
    return lifecycle;
  }

  return createTargetLifecycle({ ...lifecycle, [required]: now });
} // stampRequiredDate()

/**
 * The whole transition, for a caller that has a timestamp: clear, then stamp.
 *
 * Order matters and is why this is a function rather than a note. `mastered` clears the hold and
 * aborted dates and then records a mastery date; the other way round would clear what it just wrote.
 *
 * @param {TargetLifecycle} lifecycle
 * @param {string} nextStatus
 * @param {*} now
 * @returns {TargetLifecycle}
 */
export function advanceTo(lifecycle, nextStatus, now) {
  return stampRequiredDate(withStatus(lifecycle, nextStatus), now);
} // advanceTo()

/**
 * The furthest a target can have got, judged by its dates alone, latest stage first.
 *
 * A mastery date reads as `mastered` and never as `closed`, though both stamp it: only something
 * outside the lifecycle, such as whether the target is still maintained, tells the two apart.
 */
const STATUS_IMPLIED_BY_DATE = [
  ['abortedDate', TARGET_STATUS.ABORTED],
  ['holdDate', TARGET_STATUS.HOLD],
  ['masteryDate', TARGET_STATUS.MASTERED],
  ['introductionDate', TARGET_STATUS.ACQUISITION],
];

/**
 * The status a lifecycle's dates imply, for a source that records dates but no status.
 *
 * @param {TargetLifecycle} lifecycle
 * @returns {string|null} null when it carries no date at all, which implies nothing
 */
export function statusImpliedByDates(lifecycle) {
  return STATUS_IMPLIED_BY_DATE.find(([dateField]) => lifecycle[dateField])?.[1] ?? null;
} // statusImpliedByDates()

/**
 * Which of a lifecycle's dates disagree with its status, and how.
 *
 * TWO DIFFERENT PROBLEMS, SO TWO ANSWERS. A date the status contradicts is present and wrong — it
 * gets cleared. A date the status requires is absent, and nothing here invents one. Returning them
 * in one list made a caller word both the same way, and an importer told the user that a mastery
 * date it never received "does not match the target status".
 *
 * @param {import('./Target.types').TargetLifecycle} lifecycle
 * @returns {{ contradicted: string[], missing: string[] }} field names, each possibly empty
 */
export function dateConflicts(lifecycle) {
  const meta = TARGET_STATUS_META[lifecycle.status];
  if (!meta) {
    return { contradicted: [], missing: [] };
  }

  return {
    contradicted: meta.resetsDates.filter((dateField) => Boolean(lifecycle[dateField])),
    missing: meta.stampsDate && !lifecycle[meta.stampsDate] ? [meta.stampsDate] : [],
  };
} // dateConflicts()
